/** What "Set blackout" writes back: one inclusive local-date range, and how it joins the ranges already there. */
import { ServicePlanBlackoutRange } from '@jms/model';

/** The dialog's answer. No `_id` — the caller mints one when it appends, exactly as the section renderer does. */
export type OutlineBlackoutRange = Omit<ServicePlanBlackoutRange, '_id'>;

/** A blackout owner is a Service Plan or a Service Line; both carry the identical field. */
export interface BlackoutOwnerRanges {
  blackoutRanges?: ServicePlanBlackoutRange[];
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A range is writable only when both endpoints are real local dates and `to` is not earlier than
 * `from`. The model stores `to` as "never earlier than from", so an inverted pick is a bug the
 * dialog must refuse rather than something the API is expected to straighten out.
 */
export function isBlackoutRangeValid(range: Partial<OutlineBlackoutRange> | null | undefined): boolean {

  const from = range?.from;
  const to = range?.to;
  if (!from || !to) return false;
  if (!DATE_PATTERN.test(from) || !DATE_PATTERN.test(to)) return false;
  return to >= from;
}

/** The dialog's answer, with a blank reason dropped rather than stored as an empty string. */
export function outlineBlackoutRange(from: string, to: string, reason: string | null | undefined): OutlineBlackoutRange {

  const trimmed = (reason ?? '').trim();
  return { from, to, ...(trimmed ? { reason: trimmed } : {}) };
}

/**
 * Appends a range to what the record already carries.
 *
 * The API replaces `blackoutRanges` wholesale, so the patch has to send the full array. Re-adding a
 * range the record already has would leave two identical entries the user cannot tell apart, so an
 * exact match on all three fields is a no-op — the existing array is returned untouched.
 */
export function mergeBlackoutRange(existing: readonly ServicePlanBlackoutRange[] | null | undefined, range: OutlineBlackoutRange, mintId: () => string = () => crypto.randomUUID()): ServicePlanBlackoutRange[] {

  const ranges = [...(existing ?? [])];
  const reason = range.reason ?? undefined;
  const duplicate = ranges.some(current => current.from === range.from && current.to === range.to && (current.reason ?? undefined) === reason);
  if (duplicate) return ranges;
  ranges.push({ _id: mintId(), ...range });
  return ranges;
}

const WEEKDAY_LABELS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_LABELS: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Numeric parts of a `YYYY-MM-DD` string. Never re-parsed through `new Date(string)`, which is UTC. */
function dateParts(date: string): { year: number; month: number; day: number; weekday: number } {

  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));

  return { year, month, day, weekday: (new Date(year, month, day).getDay() + 6) % 7 };
}

/** `Thu 3 Sep 2026`, with the month and the year droppable when the other end already carries them. */
function formatEndpoint(date: string, showMonth: boolean, showYear: boolean): string {

  const { year, month, day, weekday } = dateParts(date);
  const parts = [`${WEEKDAY_LABELS[weekday]} ${day}`];
  if (showMonth) parts.push(MONTH_LABELS[month]);
  if (showYear) parts.push(String(year));

  return parts.join(' ');
}

/** Whole days from `from` to `to` inclusive — a same-day range is one day, not zero. */
function blackoutDayCount(from: string, to: string): number {

  const start = dateParts(from);
  const end = dateParts(to);
  const millis = new Date(end.year, end.month, end.day).getTime() - new Date(start.year, start.month, start.day).getTime();

  return Math.round(millis / 86400000) + 1;
}

/**
 * The one line under the calendar that says what has been picked and what is still owed.
 *
 * It replaces the From/To boxes, so it has to carry all three states on its own: nothing picked, a
 * start waiting on an end, and a closed range. A range whose ends share a month or a year prints them
 * once — `Thu 3 – Sun 6 Sep 2026`, not `Thu 3 Sep 2026 – Sun 6 Sep 2026`, which reads as two dates
 * rather than as one span.
 */
export function formatBlackoutSelection(range: Partial<OutlineBlackoutRange> | null | undefined): string {

  const from = range?.from;
  const to = range?.to;

  if (!from || !DATE_PATTERN.test(from)) return 'Pick the first day';
  if (!to || !DATE_PATTERN.test(to)) return `${formatEndpoint(from, true, true)} → pick the last day`;
  if (to < from) return 'Pick the first day';

  const days = blackoutDayCount(from, to);
  if (from === to) return `${formatEndpoint(from, true, true)} · 1 day`;

  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);

  return `${formatEndpoint(from, !sameMonth, !sameYear)} – ${formatEndpoint(to, true, true)} · ${days} days`;
}
